import { FC, useEffect, useState, useContext } from "react";
import { useLocation, useParams } from "react-router-dom";
import ListItemResult, {
  COLOURS,
} from "../../../../components/ListItemResult/ListItemResult";
import { LocationStatus } from "../../../../components/LocationButton/LocationButton";
import { RECENT_POIS_KEY } from "../../../../constants";
import useRecentQueries from "../../../../hooks/useRecentQueries";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { buildRoutingPath } from "../../../../utils/buildRoutingPath";
import { push } from "../../../../utils/navigate";
import { truncateString } from "../../../../utils/truncateString";
import { QueryParams, RoutingPath } from "../../../../utils/types";
import RoutingListItemGroup from "../RoutingListItemGroup/RoutingListItemGroup";
import { MapContext } from "../../../../contexts/MapContext";

import styles from "./RoutingQuickActions.module.scss";
import useResponsive from "../../../../hooks/useResponsive";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Dialog, Button, TextButton, FormDropdown } from "@livingmap/hippo";
import { DropdownMenuItem } from "@livingmap/hippo/dist/components/FormDropdown/FormDropdown";
import { FloorConfig } from "../../../../components/Map/plugins/floor-control";
import useLocationPermission from "../../../../hooks/useLocationPermission";
import ListItemLocationButton from "../../../../components/ListItemLocationButton/ListItemLocationButton";

interface Props {
  onChooseFromMapButtonClick?: () => void;
  recentItemsLimit?: number;
  showQuickActions?: boolean;
}

export const RoutingQuickActions: FC<Props> = ({
  onChooseFromMapButtonClick,
  recentItemsLimit = 10,
  showQuickActions = true,
}) => {
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const { Mobile: showChooseFromMap } = useResponsive();
  const { t } = useTranslation();

  const { fromName, fromId, toName, toId } = useParams();

  const { locationStatus, queryParamsConfig, project } = useAppSelector(
    (state) => state.application,
  );

  const { positionControl, floorControl, geofenceControl } =
    useContext(MapContext);
  const [isLocationErrorDialogOpen, setIsLocationErrorDialogOpen] =
    useState(false);
  const [navigatingFromPosition, setNavigatingFromPosition] = useState(false);
  const [showNavigateFromPosition, setShowNavigateFromPosition] =
    useState(true);
  const [floorSelectList, setFloorSelectList] = useState<DropdownMenuItem[]>(
    [],
  );
  const [selectedFloor, setSelectedFloor] = useState<string | undefined>(
    undefined,
  );
  const [selectedFloorObj, setSelectedFloorObj] = useState<
    FloorConfig | undefined
  >(undefined);

  const [showFloorSelector, setShowFloorSelector] = useState(false);
  const [showFloorRequest, setShowFloorRequest] = useState(false);

  const { handleLocationRequest } = useLocationPermission({
    project: project ? project : "",
    floorControl,
    positionControl,
    geofenceControl,
  });

  const { parsedRecentQueries } = useRecentQueries(RECENT_POIS_KEY, 10);
  function handleRecentPoiClick(id: string, name?: string) {
    dispatch(
      push({
        pathOrLocation: state?.selectDestination
          ? buildRoutingPath(RoutingPath.NAVIGATE, {
              fromId,
              toId: id,
            })
          : buildRoutingPath(RoutingPath.NAVIGATE, {
              fromId: id,
              toId,
            }),
        discardParams: [QueryParams.QUERY, QueryParams.SEARCH_TAG_TYPE],
      }),
    );
  }

  useEffect(() => {
    setShowNavigateFromPosition(
      locationStatus === LocationStatus.FOUND &&
        queryParamsConfig["geolocation"] !== "disable",
    );
  }, [locationStatus, queryParamsConfig]);

  useEffect(() => {
    if (!floorControl) return;

    const allFloors = floorControl.getAllFloors();
    if (allFloors) {
      const floorList = allFloors.map((floor) => {
        return { name: floor.name, value: floor.short_name };
      });
      setFloorSelectList(floorList);

      const activeFloor = floorControl.getActiveFloor();
      if (activeFloor) {
        setSelectedFloor(activeFloor.short_name);
      }
    }
  }, [floorControl]);

  useEffect(() => {
    if (locationStatus === LocationStatus.FOUND && showFloorRequest) {
      setShowFloorRequest(false);
      setShowFloorSelector(true);
    }
  }, [locationStatus, showFloorRequest]);

  useEffect(() => {
    const position = positionControl?.getCurrentPosition();
    if (
      navigatingFromPosition &&
      locationStatus === LocationStatus.FOUND &&
      position
    ) {
      let coords = `${position.longitude},${position.latitude}`;

      const options = state?.selectDestination
        ? { fromId, toId: coords, floorName: selectedFloorObj?.short_name }
        : { fromId: coords, toId, floorName: selectedFloorObj?.short_name };

      if (selectedFloorObj) {
        dispatch(
          push({
            pathOrLocation: buildRoutingPath(
              RoutingPath.NAVIGATE_FLOOR,
              options,
            ),
            discardParams: [QueryParams.QUERY, QueryParams.SEARCH_TAG_TYPE],
          }),
        );
      } else {
        dispatch(
          push({
            pathOrLocation: buildRoutingPath(RoutingPath.NAVIGATE, options),
            discardParams: [QueryParams.QUERY, QueryParams.SEARCH_TAG_TYPE],
          }),
        );
      }
    }
  }, [
    dispatch,
    toId,
    toName,
    fromId,
    fromName,
    positionControl,
    state?.selectDestination,
    navigatingFromPosition,
    selectedFloorObj,
    locationStatus,
  ]);

  const handleUserLocationPosition = () => {
    if (locationStatus === LocationStatus.DISABLED) {
      setIsLocationErrorDialogOpen(true);
    } else if (locationStatus !== LocationStatus.FOUND) {
      positionControl!.getCurrentPosition();
      handleLocationRequest();
      positionControl!.resetStabilisation();
    }
    setShowFloorRequest(true);
  };

  const handleUserFloorSubmit = (submit: boolean) => {
    if (submit && floorControl) {
      const floorObj = floorControl
        .getAllFloors()
        ?.find((floorObj) => floorObj.short_name === selectedFloor);
      setSelectedFloorObj(floorObj);
    }
    setNavigatingFromPosition(true);
  };

  const maxHeight = window.screen.height / 2 - 74;

  return (
    <div className={styles.container}>
      <Dialog
        closable={true}
        dataQA={"floor-change-dialog"}
        isOpen={showFloorSelector}
        title={t("route_page.floor_dialog_header")}
        width={320}
        footer={
          <div className={styles.buttonContainer}>
            <TextButton
              dataQA="cancel-button"
              text={t("route_page.floor_dialog_cancel_button")}
              size="regular"
              type="primary"
              underlined={false}
              onClick={() => {
                handleUserFloorSubmit(false);
              }}
            />
            <Button
              dataQA="confirm-button"
              text={t("route_page.floor_dialog_submit_button")}
              size="regular"
              type="primary"
              style="filled"
              onClick={() => {
                handleUserFloorSubmit(true);
              }}
            />
          </div>
        }
        onClose={() => {
          setShowFloorSelector(false);
        }}
      >
        {
          <div className={styles.floorDialog}>
            <p className={styles.floorText}>
              {t("route_page.floor_dialog_body")}
            </p>
            <FormDropdown
              dataQA="floor-select-dropdown"
              className={styles.floorDropdown}
              items={floorSelectList}
              onChange={(selection) => {
                setSelectedFloor(selection.value);
              }}
              tick={true}
              scroll={true}
              placeholder={""}
              maxHeight={maxHeight}
              value={floorSelectList.find((item) => {
                return item.value === selectedFloor;
              })}
            />
          </div>
        }
      </Dialog>
      <Dialog
        dataQA="location-error-dialog"
        isOpen={isLocationErrorDialogOpen}
        title={t("location_error_dialog.title")}
        width={340}
        footer={
          <div className={styles.buttonContainer}>
            <TextButton
              dataQA="location-error-dialog-cancel-button"
              text={t("location_error_dialog.cancel_button_text")}
              size="regular"
              type="primary"
              onClick={() => {
                setIsLocationErrorDialogOpen(false);
              }}
            />
            <Button
              dataQA="location-error-dialog-retry-button"
              text={t("location_error_dialog.retry_button_text")}
              size="regular"
              type="primary"
              style="filled"
              onClick={() => {
                setIsLocationErrorDialogOpen(false);
              }}
            />
          </div>
        }
      >
        {t("home.user_location_declined_alert")}
      </Dialog>
      {showQuickActions && (
        <>
          <div className={classNames(styles.quickActions)}>
            <ListItemLocationButton
              onClick={handleUserLocationPosition}
              dataQA="choose-current-location-list-item"
              title={t("route_page.your_location_button")}
              status={locationStatus}
              color={COLOURS.BLUE}
              className={styles.quickActionButton}
            />
            {showChooseFromMap && (
              <ListItemResult
                onClick={onChooseFromMapButtonClick}
                dataQA="choose-from-map-list-item"
                title={t("route_page.choose_from_map_button")}
                color={COLOURS.BLUE}
                icon="LocationIcon"
              />
            )}
          </div>
          <div className={styles.divider} />
        </>
      )}
      <div
        className={classNames(styles.recentPois, {
          [styles.noMarginTop]:
            !showQuickActions ||
            (!showChooseFromMap && !showNavigateFromPosition),
        })}
      >
        <RoutingListItemGroup
          icon="HistoryIcon"
          items={parsedRecentQueries
            .sort((a, b) => b.key - a.key)
            .filter((record) => {
              // filter out historic records with old format ids
              const re = /^[0-9]+$/;
              return !re.test(record.value);
            })
            .map(({ name, address, value }) => {
              const truncatedName = name ? truncateString(name) : "";
              const truncatedAddress = address ? truncateString(address) : "";
              return {
                name: truncatedName,
                address: truncatedAddress,
                onClick: () => handleRecentPoiClick(value, name),
              };
            })
            .slice(0, recentItemsLimit)}
        />
      </div>
    </div>
  );
};

export default RoutingQuickActions;
